'use client';

import { useCallback, useEffect, useState } from 'react';

import { PillList } from './pill';
import { BaseSelect } from './select';
import type { BaseSelectProps, SelectOption } from './select';

export type MultiSelectProps<T extends SelectOption> = BaseSelectProps<T> & {
  showPills?: boolean;
};

export const MultiSelect = <T extends SelectOption>({
  showPills = false,
  clearable = false,
  ...props
}: MultiSelectProps<T>) => {
  const [selectedItems, setSelectedItems] = useState<string[]>(
    props.defaultValue ?? [],
  );
  const [pills, setPills] = useState<T[]>(() => []);
  const [isOpen, setIsOpen] = useState(false);

  const handleRemoveItems = useCallback(
    (currentItem: T) => {
      const filteredItems = selectedItems.filter(
        (item) => item !== currentItem.value,
      );
      const filteredPills = pills.filter(
        (item) => item.value !== currentItem.value,
      );

      setSelectedItems(filteredItems);
      setPills(filteredPills);

      props.onValueChange?.({
        items: filteredPills,
        value: filteredItems,
      });
    },
    [selectedItems, setSelectedItems],
  );

  useEffect(() => {
    if (showPills && !isOpen) {
      setPills(
        props.items.filter((item) => selectedItems.includes(item.value)),
      );
    }
  }, [selectedItems, showPills, isOpen]);

  return (
    <div className="h-auto space-y-2">
      <BaseSelect
        {...props}
        multiple
        disableValueTruncation
        clearable={clearable}
        onOpenChange={({ open }) => {
          setIsOpen(open);
        }}
        value={selectedItems}
        onValueChange={(e) => {
          setSelectedItems(e.value);
          props.onValueChange?.(e);
        }}
      />
      {showPills && <PillList list={pills} onClick={handleRemoveItems} />}
    </div>
  );
};
